import { ref, computed, type Ref } from 'vue';
import type { ModuleCategory, ConfigModule } from '~/types/config';

export interface DeleteCategoriesOptions {
  /**
   * Callback when a category is marked as deleted
   */
  onCategoryDeleted?: (category: ModuleCategory) => void;

  /**
   * Callback when a category is restored
   */
  onCategoryRestored?: (category: ModuleCategory) => void;

  /**
   * Function to apply deletion to backend
   */
  applyDeletion?: (xpath: string, locale: string) => Promise<void>;
}

/**
 * Common class for managing deletion of config categories
 */
export class DeleteCategories {
  private deletedModules = ref<Record<string, ConfigModule>>({});
  private options: DeleteCategoriesOptions;

  /**
   * Constructor for DeleteCategories
   * @param {DeleteCategoriesOptions} options - Options for deletion management
   */
  constructor(options: DeleteCategoriesOptions = {}) {
    this.options = options;
  }

  /**
   * Get deleted modules as reactive reference
   * @returns {Ref<Record<string, ConfigModule>>} - Reactive reference to deleted modules
   */
  get deletedModulesRef(): Ref<Record<string, ConfigModule>> {
    return this.deletedModules;
  }

  /**
   * Get deleted modules as array for template usage
   * @returns {Ref<ConfigModule[]>} - Reactive reference to an array of deleted modules
   */
  get deletedModulesArray(): Ref<ConfigModule[]> {
    return computed(() => Object.values(this.deletedModules.value));
  }

  /**
   * Get total count of deleted categories
   * @returns {Ref<number>} - Reactive reference to the count of deleted categories
   */
  get deletedCategoriesCount(): Ref<number> {
    return computed(() =>
      Object.values(this.deletedModules.value)
        .reduce((total, module) => total + Object.keys(module.categories).length, 0)
    );
  }

  /**
   * Check if there are any deleted categories
   * @return {Ref<boolean>} - Reactive reference indicating if there are deleted categories
   */
  get hasDeletedCategories(): Ref<boolean> {
    return computed(() => this.deletedCategoriesCount.value > 0);
  }

  /**
   * Get all deleted categories
   * @returns {ModuleCategory[]} - Array of all deleted categories
   */
  get deletedCategories(): ModuleCategory[] {
    const categories: ModuleCategory[] = [];
    Object.values(this.deletedModulesRef.value).forEach(module => {
      Object.values(module.categories).forEach(category => {
        categories.push(category);
      });
    });
    return categories;
  }

  /**
   * Check if a category is marked as deleted
   * @param {string} xpath - The xpath of the category to check
   * @return {boolean} - True if the category is deleted, false otherwise
   */
  isCategoryDeleted(xpath: string): boolean {
    return Object.values(this.deletedModules.value).some(module =>
      Object.values(module.categories).some(category => category.xpath === xpath)
    );
  }

  /**
   * Get a deleted category by xpath
   * @param {string} xpath - The xpath of the category to find
   * @return {ModuleCategory | null} - The deleted category if found, otherwise null
   */
  getDeletedCategory(xpath: string): ModuleCategory | null {
    for (const module of Object.values(this.deletedModules.value)) {
      for (const category of Object.values(module.categories)) {
        if (category.xpath === xpath) {
          return category;
        }
      }
    }
    return null;
  }

  /**
   * Get deleted categories count for a specific module
   * @param {number} moduleId - The ID of the module
   * @return {number} - The count of deleted categories for the specified module
   */
  getDeletedCategoriesCountForModule(moduleId: number): number {
    const module = this.deletedModules.value[moduleId];
    return module ? Object.keys(module.categories).length : 0;
  }


  /**
   * Mark a category as deleted
   * @param {ModuleCategory} category - The category to mark as deleted
   * @param {ConfigModule} moduleInfo - The module information containing the module name and translation
   * @return {boolean} - True if the category was successfully marked as deleted, false otherwise
   */
  markCategoryAsDeleted(
    category: ModuleCategory,
    moduleInfo: ConfigModule
  ): boolean {
    if (!category.xpath?.trim()) {
      return false;
    }
    // Check if module already exists in deleted modules
    const existingModule = this.deletedModules.value[moduleInfo.moduleId];

    if (existingModule) {
      // Add category to existing module
      existingModule.categories[category.categoryName] = category;
    } else {
      // Create new module with the deleted category
      this.deletedModules.value[moduleInfo.moduleId] = {
        moduleId: moduleInfo.moduleId,
        moduleName: moduleInfo.moduleName,
        translation: moduleInfo.translation,
        xpath: moduleInfo.xpath,
        categories: {
          [category.categoryName]: category
        }
      } as ConfigModule;
    }

    // Call callback if provided
    this.options.onCategoryDeleted?.(category);

    return true;
  }

  /**
   * Restore a deleted category by xpath
   * @param {string} xpath - The xpath of the category to restore
   * @return {ModuleCategory | null} - The restored category or null if not found
   */
  restoreCategory(xpath: string): ModuleCategory | null {
    if (!xpath?.trim()) {
      return null;
    }

    // Find the category in deleted modules
    for (const [moduleId, module] of Object.entries(this.deletedModules.value)) {
      for (const [categoryName, category] of Object.entries(module.categories)) {
        if (category.xpath === xpath) {
          // Remove category from deleted module
          delete module.categories[categoryName];

          // If module has no more categories, remove the module
          if (Object.keys(module.categories).length === 0) {
            delete this.deletedModules.value[moduleId];
          }


          // Call callback if provided
          this.options.onCategoryRestored?.(category);

          return category;
        }
      }
    }

    return null;
  }

  /**
   * Restore all deleted categories
   * @return {ModuleCategory[]} - Array of restored categories
   */
  restoreAllCategories(): ModuleCategory[] {
    const restoredCategories: ModuleCategory[] = [];

    Object.values(this.deletedModules.value).forEach(module => {
      Object.values(module.categories).forEach(category => {
        this.options.onCategoryRestored?.(category);
        restoredCategories.push(category);
      });
    });

    // Clear all deleted modules
    this.deletedModules.value = {};

    return restoredCategories;
  }

  /**
   * Apply all pending deletions to backend
   */
  async applyAllDeletions(locale: string): Promise<void> {
    if (!this.options.applyDeletion) {
      throw new Error('applyDeletion function not provided');
    }

    const deletionsToApply = Object.values(this.deletedModules.value);

    for (const module of deletionsToApply) {
      for (const category of Object.values(module.categories)) {
        if (category.xpath) {
          await this.options.applyDeletion(category.xpath, locale);
        }
      }
    }

    // Clear deleted modules after successful deletion
    this.deletedModules.value = {};
  }
}